<template>
  <form
    id="module-form"
    ref="module-form"
    class="tw-max-w-full tw-mx-auto tw-flex-shrink-0 tw-mb-20 auth-card tw-flex tw-flex-wrap"
    method="post"
    action="/module/create"
    @submit.prevent="onSubmit"
  >

    <div class="tw-w-full tw-mb-4">
      <h1 class="tw-text-4xl font-title">{{ moduleId ? 'Edit Module' : 'Create a Module' }}</h1>
      <!-- <h6 class="tw-text-base opacity-78 tw-text-app-deep-blue">Please fill all inputs</h6> -->
    </div>

    <div class="tw-w-full tw-mb-4 tw-flex-shrink-0 tw-px-1">
      <InputGroup
        :class="{ '--loading-data': loadingForm }"
        label="Name"
        name="name"
        type="text"
        placeholder="name"
        input-classes="tw-p-3"
        v-model="form.name"
        :error="errors.name"
      />
    </div>
    <div class="tw-w-full tw-mb-4 tw-flex-shrink-0 tw-px-1">
      <QuillTextareaGroup
        :class="{ '--loading-data': loadingForm }"
        label="Description"
        instruct="Give a description about the module"
        name="description"
        type="text"
        placeholder="description"
        input-classes="tw-p-3 focus:tw-bg-gray-100"
        v-model="form.description"
        :rows="4"
        :error="errors.description"
        :form-data="{
          companyId: companyProgram.companyid,
          programId: companyProgram.id,
        }"
        :allow-media="true"
        :super-format="true"
        scrolling-container-selector=".modal__body"
      />
    </div>

    <div class="tw-w-full tw-mb-4 tw-flex-shrink-0 tw-px-1">
      <InputGroup
        label="Start date"
        name="start"
        type="date"
        placeholder="Start Date"
        input-classes="tw-p-1"
        v-model="form.start"
        :error="errors.start"
      />
    </div>

    <div class="tw-w-full tw-mb-4 tw-flex-shrink-0 tw-px-1">
      <InputGroup
        label="End date"
        name="end"
        type="date"
        placeholder="End Date"
        input-classes="tw-p-1"
        v-model="form.end"
        :error="errors.end"
      />
    </div>

    <div class="tw-w-full tw-mb-4 tw-flex-shrink-0 tw-px-1">
      <!--  -->
    </div>

    <div class="tw-w-full tw-flex-shrink-0 tw-px-1 tw-mb-4 tw-pt-10">
      <BaseButton
        type="submit"
        :class="[
          'tw-block tw-w-full md:tw-inline-block md:tw-w-auto tw-mb-4 tw-py-3 tw-px-8',
          `${btnClass}`
        ]"
        :text="btnText"
        :disabled="!canSubmit || btnDisabled"
      />
    </div>

  </form>
</template>

<script>
import _debounce from 'lodash/debounce';

import QuillTextareaGroup from '@/components/QuillTextareaGroup.vue';
import InputGroup from '@/components/InputGroup.vue';
import form from '@/modules/formHelper';

export default {
  name: 'EditModule',
  components: {
    QuillTextareaGroup,
    InputGroup,
  },
  props: {
    companyProgram: {
      type: Object,
      required: true,
    },
    moduleId: {
      type: [String, Number],
      default: undefined,
    },
    btnClass: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loadingForm: true,
      errorForm: undefined,
      btnText: 'Save',
      btnDisabled: false,
      form: {
        name: undefined,
        description: undefined,
        start: undefined,
        end: undefined,
        duration: undefined,
      },
      errors: {},
    };
  },
  watch: {
    // eslint-disable-next-line func-names
    'companyProgram.id': function () {
      this.resetForm();
      this.loadForm();
    },
    moduleId() {
      this.resetForm();
      this.loadForm();
    },
  },
  computed: {
    canSubmit() {
      return form.isValidTextInput(this.form.name)
        && form.isValidTextInput(this.form.description)
        && form.isValidDate(this.form.start)
        && form.isValidDate(this.form.end)
        && this.isValidDateRange
        && !this.loadingForm
        && this.companyProgram.id !== undefined;
    },
    isValidDateRange() {
      const start = this.$moment(this.form.start);
      const end = this.$moment(this.form.end);

      return end.isAfter(start);
    },
    isAnActiveModule() {
      const start = this.$moment(this.form.start);
      const end = this.$moment(this.form.end);
      const today = this.$moment();

      if (today.isAfter(start) && today.isBefore(end)) {
        return true;
      }

      return false;
    },
  },
  methods: {
    resetForm() {
      this.form = {
        name: undefined,
        description: undefined,
        start: undefined,
        end: undefined,
        duration: undefined,
      };
    },
    // eslint-disable-next-line func-names
    loadForm: _debounce(async function () {
      this.loadingForm = true;
      this.errorForm = undefined;

      // * get module details if id is defined
      if (this.moduleId) {
        const modules = await this.$store.dispatch('getProgramModules', { progid: this.companyProgram.id });
        const curr = modules.find((p) => Number(p.id) === Number(this.moduleId)) || {};

        this.form.name = curr.goal || '';
        this.form.description = curr.description || '';
        this.form.duration = curr.duration || '';
        this.form.start = curr.start || '';
        this.form.end = this.$moment(this.form.start).add(Number(curr.duration), 'days').format('YYYY-MM-DD');
      }

      await this.$nextTick();
      await this.$nextTick();

      this.loadingForm = false;
    }, 300),
    async onSubmit() {
      if (!this.canSubmit) {
        this.$toasted.global.appError({
          errorMessage: 'You can\'t submit the form. Check for empty inputs',
        });

        console.warn('Cannot submit form... \n check for empty inputs');
        return false;
      }

      this.btnDisabled = true;
      this.btnText = 'working...';

      const data = {
        ...(this.form),
        goal_name: this.form.name,
        desc: this.form.description,
        duration: this.$moment(this.form.end).diff(this.$moment(this.form.start), 'days'),
        companyid: this.companyProgram.companyid,
        progid: this.companyProgram.id,
        moduleId: this.moduleId,
      };

      let result;

      if (this.moduleId) {
        result = await this.$store.dispatch('updateProgramModule', data);
      } else {
        result = await this.$store.dispatch('createProgramModule', data);
      }

      if (this.isAnActiveModule && this.moduleId) {
        await this.$store.dispatch('activateProgramModule', this.moduleId);
      }

      if (result) {
        this.$toasted.success('Saved!', { duration: 1000 });
        this.$emit('created-success');

        // todo: update this
        // if (this.moduleId) {
        //   this.$store.dispatch('logAction', this.$store.state.Logger.actionsIndexes.editPillar);
        // } else {
        //   this.$store.dispatch('logAction', this.$store.state.Logger.actionsIndexes.addPillar);
        // }
      } else {
        this.$toasted.global.appError({
          errorMessage: 'Saving module failed. Please try again later',
        });
      }

      this.btnDisabled = false;
      this.btnText = 'save';

      return result;
    },
  },
  async created() {
    this.errors = { ...this.form };

    await this.$nextTick();
    await this.loadForm();
    await this.$nextTick();

    this.loadingForm = false;
  },
};
</script>

<style>

</style>
